import { useEffect, useMemo, useState } from 'react';
import { TaxExemptionTeamMember, TaxExemptionTeamMemberCreate } from '@dill/dill-shared';
import {
  DillModal,
  DillButton,
  DillInput,
  DillField,
  DillToggle,
  DillSelect,
} from '../../../../general/Dill';

interface SupplierUserOption {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

interface TaxExemptionTeamFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TaxExemptionTeamMemberCreate) => void;
  initialData?: TaxExemptionTeamMember | null;
  isLoading: boolean;
  errorMessage?: string | null;
  supplierUsers: SupplierUserOption[];
  existingTeamMembers: TaxExemptionTeamMember[];
}

const CUSTOM_PERSON_VALUE = '';

export const TaxExemptionTeamFormModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading,
  errorMessage,
  supplierUsers,
  existingTeamMembers,
}: TaxExemptionTeamFormModalProps) => {
  const [selectedUserId, setSelectedUserId] = useState<string>(CUSTOM_PERSON_VALUE);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const isEditMode = !!initialData;

  useEffect(() => {
    if (!open) {
      return;
    }
    setHasAttemptedSubmit(false);
    if (initialData) {
      setSelectedUserId(initialData.userId || CUSTOM_PERSON_VALUE);
      setName(initialData.name || '');
      setEmail(initialData.email || '');
      setPhone(initialData.phone || '');
      setIsActive(initialData.isActive !== false);
    } else {
      setSelectedUserId(CUSTOM_PERSON_VALUE);
      setName('');
      setEmail('');
      setPhone('');
      setIsActive(true);
    }
  }, [open, initialData]);

  // A teammate already linked to another team entry shouldn't be offered again - except the one
  // currently backing this entry in edit mode, which must stay selectable as itself.
  const usedUserIds = useMemo(
    () =>
      new Set(
        existingTeamMembers
          .filter((member) => member.userId && member.id !== initialData?.id)
          .map((member) => member.userId as string)
      ),
    [existingTeamMembers, initialData?.id]
  );

  const availableSupplierUsers = useMemo(
    () => supplierUsers.filter((u) => !usedUserIds.has(u.id)),
    [supplierUsers, usedUserIds]
  );

  const userOptions = useMemo(
    () => [
      { label: '— Add a new person —', value: CUSTOM_PERSON_VALUE },
      ...availableSupplierUsers.map((u) => ({
        label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
        value: u.id,
      })),
    ],
    [availableSupplierUsers]
  );

  const isFromExistingUser = selectedUserId !== CUSTOM_PERSON_VALUE;

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
    if (userId) {
      const user = supplierUsers.find((u) => u.id === userId);
      if (user) {
        setName([user.firstName, user.lastName].filter(Boolean).join(' ') || user.email);
        setEmail(user.email || '');
      }
    }
  };

  const isNameValid = name.trim().length > 0;
  const isValid = isNameValid;

  const showError = (fieldIsValid: boolean) => hasAttemptedSubmit && !fieldIsValid;

  const handleSubmit = () => {
    if (!isValid) {
      setHasAttemptedSubmit(true);
      return;
    }
    onSubmit({
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      isActive,
      userId: isFromExistingUser ? selectedUserId : null,
    });
  };

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit tax exemption team member' : 'New tax exemption team member'}
      subtitle="Only members with Dill access can be auto-assigned tax exemption dispute tasks."
      maxWidth="420px"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={onClose} />
          <DillButton
            label={isEditMode ? 'Save changes' : 'Add team member'}
            onClick={handleSubmit}
            isDisabled={!isValid}
            isLoading={isLoading}
          />
        </>
      }>
      {errorMessage && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 m-0">
          {errorMessage}
        </p>
      )}

      {(availableSupplierUsers.length > 0 || isFromExistingUser) && (
        <DillField label="Add from an existing teammate, or add a new person">
          <DillSelect value={selectedUserId} onChange={handleSelectUser} options={userOptions} />
        </DillField>
      )}

      <DillField
        label="Name"
        required
        error={showError(isNameValid) ? 'A name is required' : undefined}>
        <DillInput
          value={name}
          onChange={setName}
          placeholder="e.g. Jamie Rivera"
          disabled={isFromExistingUser}
        />
      </DillField>

      <DillField label="Email (optional)">
        <DillInput
          value={email}
          onChange={setEmail}
          placeholder="jamie@example.com"
          disabled={isFromExistingUser}
        />
      </DillField>

      <DillField label="Phone (optional)">
        <DillInput value={phone} onChange={setPhone} placeholder="(555) 555-5555" />
      </DillField>

      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600">Active</label>
        <DillToggle value={isActive} onChange={setIsActive} />
      </div>
    </DillModal>
  );
};

export default TaxExemptionTeamFormModal;
